// FinalPass (§12.8): one custom pass in linear space before OutputPass.
//   1. Tilt-shift: a vertical-gradient blur, 9 taps per direction on High/Medium and 5 on Low, with
//      the focus band (width 0.35) centered on the camera target's screen Y; max 2.5 px at 1080p.
//   2. Grade: lift / gamma / gain, saturation and a tint, per mode — normal, gold (Golden Round),
//      ghost (local is a ghost) and GAME! (saturation 0 everywhere except a soft circle around the
//      winner, radius 2× the winner's projected height, on every tier).
//   3. Vignette 0.25, a radial chromatic-aberration pulse, film grain 0.03 (off on Low).
//   4. VHS mode for replays: scanlines, 0.004 chroma split and a tracking-line wobble; plus the
//      heavier "rewind" tear used for the VHS wipe.
// Extras driven by §11 rows: the Sudden Death red vignette pulse, MATCH POINT letterbox bars, the
// bomb's screen-space distortion ring (High) and a soft flash (never white under Reduced Flashing).
// The same material also serves the Potato tier's single fallback pass (rendered to the screen, where
// its tone-mapping and color-space chunks apply; into a render target they are no-ops).
import { Color, ShaderMaterial, Vector2, Vector3, Vector4, type Texture } from 'three';

export interface FinalUniforms {
  tDiffuse: { value: Texture | null };
  uRes: { value: Vector2 };
  uFocusY: { value: number };
  uFocusW: { value: number };
  uBlurPx: { value: number };
  uLift: { value: Vector3 };
  uGamma: { value: Vector3 };
  uGain: { value: Vector3 };
  uSat: { value: number };
  uContrast: { value: number };
  uTint: { value: Color };
  uTintAmt: { value: number };
  uGame: { value: number };
  uGameCenter: { value: Vector2 };
  uGameRadius: { value: number };
  uVignette: { value: number };
  uVigColor: { value: Color };
  uRed: { value: number };
  uChroma: { value: number };
  uGrain: { value: number };
  uTime: { value: number };
  uVhs: { value: number };
  uWipe: { value: number };
  uLetter: { value: number };
  uFlash: { value: number };
  uFlashColor: { value: Color };
  uWave: { value: Vector4 };
}

export const FINAL_VERTEX = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

export const FINAL_FRAGMENT = /* glsl */ `
#include <common>
uniform sampler2D tDiffuse;
uniform vec2 uRes;
uniform float uFocusY;
uniform float uFocusW;
uniform float uBlurPx;
uniform vec3 uLift;
uniform vec3 uGamma;
uniform vec3 uGain;
uniform float uSat;
uniform float uContrast;
uniform vec3 uTint;
uniform float uTintAmt;
uniform float uGame;
uniform vec2 uGameCenter;
uniform float uGameRadius;
uniform float uVignette;
uniform vec3 uVigColor;
uniform float uRed;
uniform float uChroma;
uniform float uGrain;
uniform float uTime;
uniform float uVhs;
uniform float uWipe;
uniform float uLetter;
uniform float uFlash;
uniform vec3 uFlashColor;
uniform vec4 uWave;
varying vec2 vUv;

float hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

// The Potato fallback reads a copy of the finished (tone-mapped, sRGB) frame: decode it to linear.
vec3 fetch(vec2 uv) {
  vec3 t = texture2D(tDiffuse, uv).rgb;
#ifdef INPUT_SRGB
  t = mix(t / 12.92, pow((t + 0.055) / 1.055, vec3(2.4)), step(0.04045, t));
#endif
  return t;
}

vec3 tiltShift(vec2 uv) {
  vec3 c = fetch(uv);
#if TAPS > 0
  float d = abs(uv.y - uFocusY);
  float r = uBlurPx * smoothstep(uFocusW * 0.5, uFocusW * 0.5 + 0.3, d);
  if (r > 0.3) {
    const int H = TAPS / 2;
    vec2 px = 1.0 / uRes;
    float step = r / float(H);
    vec3 acc = c;
    float wsum = 1.0;
    for (int i = 1; i <= H; i++) {
      float o = float(i) * step;
      float w = exp(-float(i * i) / float(H * H) * 2.0);
      acc += (fetch(uv + vec2(o * px.x, 0.0)) + fetch(uv - vec2(o * px.x, 0.0))
            + fetch(uv + vec2(0.0, o * px.y)) + fetch(uv - vec2(0.0, o * px.y))) * w;
      wsum += 4.0 * w;
    }
    c = acc / wsum;
  }
#endif
  return c;
}

void main() {
  vec2 uv = vUv;
  float aspect = uRes.x / uRes.y;

  // Bomb shockwave: a refracting ring that races outward (High only; strength 0 elsewhere).
  if (uWave.w > 0.0) {
    vec2 d = uv - uWave.xy;
    d.x *= aspect;
    float dist = length(d);
    float k = exp(-pow((dist - uWave.z) / 0.03, 2.0)) * uWave.w;
    uv -= (d / max(dist, 1e-4)) * vec2(1.0 / aspect, 1.0) * k * 0.025;
  }

  // VHS: tracking wobble, a rolling tracking band and (while rewinding) heavy horizontal tears.
  float vhs = max(uVhs, uWipe);
  if (vhs > 0.0) {
    float line = floor(uv.y * uRes.y * 0.5);
    float wob = sin(uv.y * 40.0 + uTime * 9.0) * 0.0012 + (hash12(vec2(line, floor(uTime * 30.0))) - 0.5) * 0.0016;
    float band = smoothstep(0.035, 0.0, abs(fract(uv.y * 0.6 - uTime * 0.18) - 0.5) - 0.44);
    uv.x += (wob + band * 0.006) * vhs;
    float tear = step(0.82, hash12(vec2(floor(uv.y * 24.0), floor(uTime * 24.0)))) * uWipe;
    uv.x += tear * (hash12(vec2(floor(uTime * 24.0), uv.y)) - 0.5) * 0.12;
  }

  vec3 c = tiltShift(uv);

  float ca = uChroma + 0.004 * vhs;
  if (ca > 0.0) {
    vec2 dir = (uv - 0.5);
    vec2 off = uVhs > 0.0 || uWipe > 0.0 ? vec2(ca, 0.0) : dir * ca * 2.0;
    c.r = fetch(uv + off).r;
    c.b = fetch(uv - off).b;
  }

  // Grade: lift / gamma / gain, then saturation (GAME! keeps color only around the winner) and tint.
  c = max(c, 0.0);
  c = uGain * (c + uLift * (1.0 - c));
  c = pow(max(c, vec3(0.0)), 1.0 / uGamma);
  // Contrast: a slope in log space around linear mid grey (0.18), so shadows deepen and highlights
  // open up without clipping (the tone mapper rolls them off).
  c = 0.18 * pow(c / 0.18 + 1e-5, vec3(uContrast));
  float lum = dot(c, vec3(0.2126, 0.7152, 0.0722));
  float sat = uSat;
  if (uGame > 0.0) {
    vec2 g = uv - uGameCenter;
    g.x *= aspect;
    float keep = 1.0 - smoothstep(uGameRadius * 0.6, uGameRadius, length(g));
    sat = mix(sat, sat * keep, uGame);
    c = mix(c, c * (0.82 + 0.18 * keep) + 0.04 * (1.0 - keep), uGame);
  }
  c = mix(vec3(lum), c, sat);
  c *= mix(vec3(1.0), uTint, uTintAmt);

  // Vignette (ink-tinted), the Sudden Death red pulse, letterbox bars.
  vec2 vq = (vUv - 0.5) * vec2(aspect, 1.0);
  float edge = smoothstep(0.35, 1.0, length(vq) / (0.5 * sqrt(aspect * aspect + 1.0)) * 1.25);
  c = mix(c, c * uVigColor, edge * uVignette);
  c = mix(c, vec3(1.0, 0.12, 0.1) * max(lum, 0.25) * 1.4, edge * uRed);
  if (vUv.y < uLetter || vUv.y > 1.0 - uLetter) c = uVigColor * 0.08;

  // VHS scanlines and noise.
  if (vhs > 0.0) {
    float scan = 0.5 + 0.5 * sin(vUv.y * uRes.y * 3.14159 * 0.5);
    c *= 1.0 - vhs * 0.16 * scan;
    float n = hash12(vUv * uRes + uTime * 61.0);
    c += (n - 0.5) * 0.06 * vhs;
    c = mix(c, vec3(dot(c, vec3(0.299, 0.587, 0.114))) * vec3(1.02, 1.0, 1.08), 0.15 * vhs);
  }

  if (uGrain > 0.0) c += (hash12(vUv * uRes + fract(uTime) * 917.0) - 0.5) * uGrain * (1.0 - 0.5 * lum);
  c = mix(c, uFlashColor, uFlash);

  gl_FragColor = vec4(max(c, 0.0), 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

export function createFinalUniforms(): FinalUniforms {
  return {
    tDiffuse: { value: null },
    uRes: { value: new Vector2(1, 1) },
    uFocusY: { value: 0.5 },
    uFocusW: { value: 0.35 },
    uBlurPx: { value: 2.5 },
    uLift: { value: new Vector3(0, 0, 0) },
    uGamma: { value: new Vector3(1, 1, 1) },
    uGain: { value: new Vector3(1, 1, 1) },
    uSat: { value: 1 },
    uContrast: { value: 1 },
    uTint: { value: new Color(1, 1, 1) },
    uTintAmt: { value: 0 },
    uGame: { value: 0 },
    uGameCenter: { value: new Vector2(0.5, 0.5) },
    uGameRadius: { value: 0.2 },
    uVignette: { value: 0.25 },
    uVigColor: { value: new Color('#2A1A3A') },
    uRed: { value: 0 },
    uChroma: { value: 0 },
    uGrain: { value: 0.03 },
    uTime: { value: 0 },
    uVhs: { value: 0 },
    uWipe: { value: 0 },
    uLetter: { value: 0 },
    uFlash: { value: 0 },
    uFlashColor: { value: new Color(1, 1, 1) },
    uWave: { value: new Vector4(0, 0, 0, 0) },
  };
}

/**
 * The FinalPass material with `taps` per tilt-shift direction (9, 5, or 0 = off). `toScreen` draws
 * straight to the canvas (the Potato fallback); with `srgbInput` it reads a copy of the finished,
 * already tone-mapped frame (so it decodes sRGB and does not tone map again).
 */
export function createFinalMaterial(uniforms: FinalUniforms, taps: number, toScreen: boolean, srgbInput = false): ShaderMaterial {
  const defines: Record<string, number | string> = { TAPS: taps };
  if (srgbInput) defines.INPUT_SRGB = '';
  return new ShaderMaterial({
    name: 'FinalPass',
    uniforms: uniforms as unknown as Record<string, { value: unknown }>,
    defines,
    vertexShader: FINAL_VERTEX,
    fragmentShader: FINAL_FRAGMENT,
    depthTest: false,
    depthWrite: false,
    // Only the Potato fallback renders straight to the screen, where tone mapping belongs here (unless
    // its input is the already tone-mapped frame).
    toneMapped: toScreen && !srgbInput,
  });
}

/** Grade presets (§12.8). Values are linear-space multipliers. */
export interface Grade { lift: number; gamma: number; gain: number; sat: number; contrast: number; tint: string; tintAmt: number }
export const GRADES: Readonly<Record<'normal' | 'gold' | 'ghost' | 'game', Grade>> = {
  // Toy-box look: punchy saturation and a log-space contrast slope, no lift (live review: lifted
  // shadows plus fog read as a milky veil over every arena; integration art pass, INTEGRATION.md).
  normal: { lift: 0, gamma: 1.0, gain: 1.0, sat: 1.12, contrast: 1.12, tint: '#FFFFFF', tintAmt: 0 },
  gold: { lift: 0, gamma: 1.0, gain: 1.03, sat: 1.16, contrast: 1.12, tint: '#FFD9A0', tintAmt: 0.15 },
  // Ghost: colour drains and cools, but the frame keeps its contrast (the old lift washed it out).
  ghost: { lift: 0, gamma: 1.0, gain: 0.97, sat: 0.72, contrast: 1.08, tint: '#B8C8FF', tintAmt: 0.16 },
  game: { lift: 0, gamma: 1.0, gain: 1.0, sat: 1.12, contrast: 1.12, tint: '#FFFFFF', tintAmt: 0 },
};
